/**
 * @module support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // Need to support all data types
// Core
import { Injectable, EventEmitter } from '@angular/core';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';

// interfaces

import { AddressValidationRequest } from '@support/api/proxies/requests/api-2004.interface';
import { APIError } from '@support/api/common/error.interface';
import {
  AddressValidationResponse,
  Address,
} from '@support/shared/widgets/components/address-form/interfaces/api-response';
import {
  AddressFormComponentInput,
  AddressFormData,
} from '@support/shared/widgets/interfaces/address-form.interface';

// Services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CustomerAddresses } from '@experience/shop/cart/modules/line-setup/interfaces/line-setup.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { Addresses } from '@support/api/proxies/responses/api-2801.interface';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
@Injectable()
export class AddressFormService {
  /**
   * Stores the Form group for the address form
   */
  public addressFormObject: AddressFormComponentInput;
  /**
   * Stores the Form group copy for the address form copy
   */
  public addressFormObjectCopy: AddressFormComponentInput;
  /**
   * Stores boolean flag for resetting address form
   */
  public addressReset: Boolean;
  /**
   * Changes for selected address
   */
  public changeFormEmitter: EventEmitter<AddressFormData>;

  constructor(
    private apiGatewayService: ApiGatewayService,
    private objectService: ObjectService,
    private stateStoreUtilService: StateStoreUtilsService
  ) {
    this.addressReset = true;
    this.changeFormEmitter = new EventEmitter<AddressFormData>();
  }

  /**
   * Method to validate the address from the API
   * @param form: Stores the value of address to be passed
   * @returns AddressValidationResponse
   */
  public validateAddress(form: Address): Observable<AddressValidationResponse> {
    const request = this.getAddressVerificationRequest(form);
    const headers = new HttpHeaders();
    return this.apiGatewayService
      .postRequest(API_MAP.API_2004_addressValidation, request, headers, null, null)
      .pipe(
        map((response: HttpResponse<AddressValidationResponse>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * method to set the form object
   */
  public setAddressForm(addressForm: AddressFormComponentInput): void {
    this.addressFormObject = addressForm;
  }

  /**
   * method to get the form object
   */
  public getAddressForm(): AddressFormComponentInput {
    return this.addressFormObject;
  }

  /**
   * method to set the form object copy
   */
  public setAddressFormCopy(addressForm: AddressFormComponentInput): void {
    this.addressFormObjectCopy = addressForm;
  }

  /**
   * method to get the form object copy
   */
  public getAddressFormCopy(): AddressFormComponentInput {
    return this.addressFormObjectCopy;
  }

  /**
   * method to set the address reset boolean
   */
  public setaddressReset(reset: Boolean): void {
    this.addressReset = reset;
  }
  /**
   * method to get the address reset boolean
   */
  public getaddressReset(): Boolean {
    return this.addressReset;
  }
  /**
   * Method to get list of addresses available for the customer from API (2006)
   * @returns Addresses
   */
  public getCustomerPaymentAddresses(): Addresses {
    const customerAddress: CustomerAddresses = {};
    const savedAddress = this.objectService.get(
      this.stateStoreUtilService.getBillingBlockDetails(),
      'billingAddress'
    );
    const customerName = this.stateStoreUtilService.getOrderSummaryData();
    const two = 2;
    if (savedAddress) {
      customerAddress.billing = {
        address1: this.objectService.get(savedAddress, 'addressLine1'),
        address2: this.objectService.get(savedAddress, 'addressLine2'),
        state: this.objectService.get(savedAddress, 'stateCode'),
        city: this.objectService.get(savedAddress, 'cityName'),
        zipCode: this.objectService.get(savedAddress, 'zip', '').split('-')[0],
        firstName: this.objectService.get(customerName, 'customerFullName', '').split(' ')[0],
        middleName: this.objectService.get(customerName, 'customerFullName', '').split(' ')[1],
        lastName: this.objectService.get(customerName, 'customerFullName', '').split(' ')[two],
      };
    }
    return customerAddress.billing ? this.getAddressObject(customerAddress.billing) : undefined;
  }
  /**
   * Method to get Address object from CustomerAddressObject
   * @param inputAddress: Addresses
   * @returns Addresses
   */
  private getAddressObject(inputAddress: Addresses): Addresses {
    return {
      firstName: inputAddress.firstName,
      middleName: inputAddress.middleName,
      lastName: inputAddress.lastName,
      address1: inputAddress.address1,
      address2: inputAddress.address2,
      city: inputAddress.city,
      state: inputAddress.state,
      zipCode: inputAddress.zipCode,
    };
  }
  /** Get address verification request
   * @returns AddressValidationRequest
   */
  private getAddressVerificationRequest(form: Address): AddressValidationRequest {
    const returnObj = {
      addressLine1: form.address1,
      addressLine2: this.objectService.get(form, 'address2') ? form.address2 : '',
      cityName: form.city,
      urbanization: this.objectService.get(form, 'urbanisation')
        ? this.objectService.get(form, 'urbanisation')
        : '',
      postalCode: form.zipCode || form.postalCode,
      stateCode: form.state,
      validationPurpose: '',
    };
    if (this.objectService.get(form, 'urbanizationCode')) {
      returnObj.urbanization = this.objectService.get(form, 'urbanizationCode');
    }
    if (this.objectService.get(form, 'validationPurpose')) {
      returnObj.validationPurpose = this.objectService.get(form, 'validationPurpose');
    }
    return returnObj;
  }
}
